/**
 * Registration Flow Component
 *
 * Orchestrates the complete registration flow including:
 * 1. Sign in/Sign up toggle (existing users can sign in directly)
 * 2. Age verification for NEW signups only (must be 18+)
 * 3. Account creation
 *
 * COPPA-compliant: Users 13-17 must be invited by a family adult (not self-register)
 */

import React, { useState } from 'react';
import AgeVerification from '../AgeVerification';
import UnderageMessage from '../UnderageMessage';
import ModernLogin from './ModernLogin';

type RegistrationStep = 'login-or-signup' | 'age-verification' | 'underage' | 'teen-invite-required' | 'registration' | 'complete';

interface UserAgeData {
  age: number;
  birthDate: string;
  needsParentalConsent: boolean;
}

const RegistrationFlow: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('login-or-signup');
  const [userAgeData, setUserAgeData] = useState<UserAgeData | null>(null);

  const handleAgeVerification = (isAdult: boolean, age: number, birthDate: string) => {
    console.log('[RegistrationFlow] Age verification completed:', { isAdult, age });

    if (age < 13) {
      // Under 13 - show underage message (8-12 PIN guidance or under 8 too young)
      const ageData: UserAgeData = {
        age,
        birthDate,
        needsParentalConsent: false
      };
      setUserAgeData(ageData);
      setCurrentStep('underage');
      return;
    }

    // Store age data
    const ageData: UserAgeData = {
      age,
      birthDate,
      needsParentalConsent: age < 18
    };
    setUserAgeData(ageData);

    // Store birth date for later use in registration
    sessionStorage.setItem('user_birth_date', birthDate);
    sessionStorage.setItem('user_age', age.toString());

    if (age < 18) {
      // 13-17: Must be invited by a family adult (COPPA compliance)
      console.log('[RegistrationFlow] User is 13-17, showing invite required message');
      setCurrentStep('teen-invite-required');
    } else {
      // 18+: Proceed directly to registration
      console.log('[RegistrationFlow] User is 18+, proceeding to registration');
      setCurrentStep('registration');
    }
  };

  const handleBackToAgeVerification = () => {
    setCurrentStep('age-verification');
    setUserAgeData(null);
    sessionStorage.removeItem('user_birth_date');
    sessionStorage.removeItem('user_age');
  };

  // Render appropriate step
  switch (currentStep) {
    case 'login-or-signup':
      // Show ModernLogin with both sign in and sign up options
      // ModernLogin handles the toggle internally
      return (
        <ModernLogin
          onSignUpClick={() => {
            // When user clicks "Sign Up", trigger age verification
            console.log('[RegistrationFlow] User clicked Sign Up, showing age verification');
            setCurrentStep('age-verification');
          }}
        />
      );

    case 'age-verification':
      return (
        <AgeVerification
          onVerificationComplete={handleAgeVerification}
          onBack={handleBackToAgeVerification}
        />
      );

    case 'underage':
      return (
        <UnderageMessage
          age={userAgeData?.age}
          onBack={handleBackToAgeVerification}
        />
      );

    case 'teen-invite-required':
      // Show message that teens must be invited by a family adult
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Family Invite Required</h2>
            <p className="text-gray-600 mb-6">
              Users ages 13-17 need to be invited by a family adult to join HoneyDo.
              Ask a parent or guardian to invite you from their account.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>How it works:</strong> Your parent or guardian can invite you
                by going to Family Settings → Invite Member in their HoneyDo app.
              </p>
            </div>
            <button
              onClick={handleBackToAgeVerification}
              className="text-amber-600 hover:text-amber-700 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>
      );

    case 'registration':
      // Show the normal ModernLogin component for registration
      // The age data is stored in sessionStorage and can be included in the registration API call
      return <ModernLogin initialMode="signup" />;

    case 'complete':
      return <ModernLogin />;

    default:
      return (
        <ModernLogin
          onSignUpClick={() => {
            console.log('[RegistrationFlow] User clicked Sign Up, showing age verification');
            setCurrentStep('age-verification');
          }}
        />
      );
  }
};

export default RegistrationFlow;
